import axios, { AxiosError } from "axios";
import { API } from "../constants";
import { toast } from "react-toastify";
import { getUserData } from "./utils";
import { loginRequest, msalInstance } from "../msalInstance";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

const refreshAuthToken = async () => {
  try {
    const userDetails = getUserData();
    const userId = userDetails?.["cognito:username"];
    if (!userId) {
      return Promise.reject("User not logged in");
    }
    const refreshResult = await axiosInstance.post(
      `/${API.AUTH.REFRESH_AUTH_TOKENS}`,
      { userId },
      {
        withCredentials: true,
      }
    );

    if (refreshResult.data?.status === "ERROR") {
      throw new Error("Failed to fetch refresh token");
    }

    return refreshResult;
  } catch (refreshErr) {
    console.log("refreshErr", refreshErr);
    return Promise.reject(refreshErr);
  }
};

const refreshEntraToken = async () => {
  try {
    const account = msalInstance.getActiveAccount();

    if (!account) {
      throw new Error("No active Entra account found");
    }

    const response = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account: account,
      forceRefresh: true,
    });

    if (!response.idToken) {
      throw new Error("Failed to acquire new ID token");
    }

    const backendResponse = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/${API.AUTH.ENTRA_CALLBACK}`,
      {
        idToken: response.idToken,
      },
      {
        withCredentials: true,
      }
    );

    if (
      backendResponse.data?.status === "SUCCESS" &&
      backendResponse.data?.csrfToken
    ) {
      sessionStorage.setItem("csrfToken", backendResponse.data.csrfToken);
    }

    return backendResponse;
  } catch (error: any) {
    if (error instanceof InteractionRequiredAuthError) {
      console.error("Interactive login required - redirecting to login");
      sessionStorage.clear();
      msalInstance.clearCache();
      window.location.href = "/";
      throw new Error("Interactive login required");
    }

    console.error("Entra token refresh failed:", error);
    throw error;
  }
};

let isTokenRefreshing = false;
let tokenRefreshPromise: Promise<any> | null = null;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true,
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError | any) => {
    const csrf = sessionStorage.getItem("csrfToken");

    if (!csrf) {
      return Promise.reject("Session expired. Please log in again.");
    }

    const isEntraAuth = sessionStorage.getItem("entraLogin") === "true";

    const isAuthError =
      error.response &&
      (error.response.status === 401 || error.response.status === 403) &&
      (error.response.data?.message === "Failed to authenticate token." ||
        error.response.data?.message === "Token expired." ||
        error.response.data?.message === "Invalid or expired token.");

    if (isAuthError) {
      try {
        if (!isTokenRefreshing) {
          isTokenRefreshing = true;

          if (isEntraAuth) {
            tokenRefreshPromise = refreshEntraToken();
          } else {
            tokenRefreshPromise = refreshAuthToken();
          }
        }

        await tokenRefreshPromise;

        return axiosInstance(error.config);
      } catch (refreshErr: any) {
        toast.dismiss();

        const errorMessage = isEntraAuth
          ? "Your session has expired. Please sign in again."
          : error.response?.data?.message ||
            "Authentication failed. Please log in again.";

        toast.info(errorMessage, { autoClose: false, closeOnClick: true });

        sessionStorage.clear();
        if (isEntraAuth) {
          msalInstance.clearCache();
        }

        setTimeout(() => {
          window.location.href = "/";
        }, 1000);

        return Promise.reject({
          err: refreshErr,
          message: errorMessage,
        });
      } finally {
        isTokenRefreshing = false;
        tokenRefreshPromise = null;
      }
    }

    return Promise.reject(error);
  }
);

axiosInstance.interceptors.request.use((request) => {
  request.withCredentials = true;

  const unsafeMethods = ["POST", "PUT", "DELETE", "PATCH"];
  if (unsafeMethods.includes(request.method?.toUpperCase() || "")) {
    const csrfToken = sessionStorage.getItem("csrfToken");
    if (csrfToken) {
      request.headers["x-csrf-token"] = csrfToken;
    }
  }

  return request;
});

export default axiosInstance;
